import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Settings2, Edit, Download, History, Receipt, Clock, Plus, UserPlus } from 'lucide-react';
import { ColumnConfiguratorDrawer } from '@/components/column-configurator-drawer';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { RateCardSheet } from '@/components/RateCard';
import { AssignmentSheet } from '@/components/assignment-sheet';
import { 
  RESOURCE_BUDGET_COLUMNS, 
  RESOURCE_BUDGET_PRESETS,
  RESOURCE_BUDGET_STORAGE_KEY 
} from '@/lib/resource-budget-columns';
import { 
  formatHours, 
  formatCurrency, 
  formatPercent,
  getResourceBudgetStatus,
  getResourceBudgetStatusLabel,
  getResourceBudgetStatusColor,
  getTeamAllocationStatus,
  getTeamAllocationStatusLabel,
  getTeamAllocationStatusColor
} from '@/lib/budget-utils';
import type { ResourceBudget } from '@/types/budget.types';
import type { Employee, ResourceAllocation } from '@/types';
import type { AssignmentSelection } from '@/types/assignment.types';

interface ResourceBudgetViewProps {
  projectId: number;
  data: ResourceBudget;
  onEdit?: () => void;
  onExport?: () => void;
  onHistory?: () => void;
  availablePeople?: Employee[];
  existingAllocations?: ResourceAllocation[];
  projectStartDate?: string;
  projectEndDate?: string;
  onAssign?: (role: string, selections: AssignmentSelection[]) => void;
}

export function ResourceBudgetView({ projectId, data, onEdit, onExport, onHistory, availablePeople, existingAllocations, projectStartDate, projectEndDate, onAssign }: ResourceBudgetViewProps) {
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);
  const [assignSheetOpen, setAssignSheetOpen] = useState(false);
  const [assignRoleName, setAssignRoleName] = useState('');
  const [assignRequiredHours, setAssignRequiredHours] = useState(0);

  console.log('[ResourceBudgetView] Received projectId:', projectId, 'type:', typeof projectId);

  if (!data || !data.summary || !data.byRole) {
    console.log('[ResourceBudgetView] Missing required data:', {
      hasData: !!data,
      hasSummary: !!data?.summary,
      hasByRole: !!data?.byRole,
    });
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Card className="w-full max-w-md border-dashed">
          <CardContent className="flex flex-col items-center justify-center text-center p-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Resource Budget</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Plan team capacity and track hours by creating a resource budget for this project.
            </p>
            {onEdit && (
              <Button onClick={onEdit}>
                <Plus className="mr-2 h-4 w-4" />
                Create Resource Budget
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!projectId) {
    console.error('[ResourceBudgetView] Missing projectId!');
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Invalid project ID</p>
      </div>
    );
  }

  const { summary, byRole, byPerson } = data;
  console.log('[ResourceBudgetView] Summary:', summary);
  console.log('[ResourceBudgetView] ByRole count:', byRole.length);

  const handleAssignClick = (roleName: string, remainingHours: number) => {
    setAssignRoleName(roleName);
    setAssignRequiredHours(remainingHours);
    setAssignSheetOpen(true);
  };

  const handleAssignConfirm = (selections: AssignmentSelection[]) => {
    if (onAssign) {
      onAssign(assignRoleName, selections);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Resource Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
            )}
            {onExport && (
              <Button variant="outline" size="sm" onClick={onExport}>
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            )}
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
            <ColumnConfigButton />
          </div>
        </CardHeader>
        <CardContent>
          <HoursByRoleView
            data={byRole}
            onAssignClick={onAssign ? handleAssignClick : undefined}
          />
        </CardContent>
      </Card>

      {/* Rate Card Sheet */}
      <RateCardSheet
        open={isRateCardOpen}
        onOpenChange={setIsRateCardOpen}
        projectId={String(projectId)}
      />

      {/* Assignment Sheet */}
      {availablePeople && existingAllocations && projectStartDate && projectEndDate && (
        <AssignmentSheet
          open={assignSheetOpen}
          onOpenChange={setAssignSheetOpen}
          roleName={assignRoleName}
          requiredHours={assignRequiredHours}
          projectId={projectId}
          projectStartDate={projectStartDate}
          projectEndDate={projectEndDate}
          availablePeople={availablePeople}
          existingAllocations={existingAllocations}
          onConfirm={handleAssignConfirm}
        />
      )}
    </div>
  );
}

// Column Configuration Button Component
function ColumnConfigButton() {
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const { columns, updateColumns } = useColumnPreferences({
    storageKey: RESOURCE_BUDGET_STORAGE_KEY,
    defaultColumns: RESOURCE_BUDGET_COLUMNS,
  });

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsConfigOpen(true)}
        className="gap-2"
      >
        <Settings2 className="h-4 w-4" />
        Configure Columns
      </Button>
      <ColumnConfiguratorDrawer
        open={isConfigOpen}
        onOpenChange={setIsConfigOpen}
        columns={columns}
        onSave={updateColumns}
        presets={RESOURCE_BUDGET_PRESETS}
        defaultColumns={RESOURCE_BUDGET_COLUMNS}
      />
    </>
  );
}

// Budget by Role View
function HoursByRoleView({ data, onAssignClick }: { data: ResourceBudget['byRole']; onAssignClick?: (roleName: string, remainingHours: number) => void }) {
  const { columns, isLoading } = useColumnPreferences({
    storageKey: RESOURCE_BUDGET_STORAGE_KEY,
    defaultColumns: RESOURCE_BUDGET_COLUMNS,
  });

  if (isLoading) {
    return <p className="text-muted-foreground text-center py-8">Loading...</p>;
  }

  if (!data || data.length === 0) {
    return <p className="text-muted-foreground text-center py-8">No role data available</p>;
  }

  // Get visible columns in order
  const visibleColumns = columns.filter((col) => col.visible).sort((a, b) => a.order - b.order);

  const rows = data.map((role) => {
    const status = getResourceBudgetStatus(role.actualHours, role.budgetHours);
    const statusColors = getResourceBudgetStatusColor(status);
    const statusLabel = getResourceBudgetStatusLabel(status);
    const varianceHours = role.actualHours - role.budgetHours;
    
    const teamAllocationStatus = getTeamAllocationStatus(role.allocatedHours || 0, role.budgetHours);
    const teamAllocationColors = getTeamAllocationStatusColor(teamAllocationStatus);
    const teamAllocationLabel = getTeamAllocationStatusLabel(teamAllocationStatus);
    
    return {
      id: role.role,
      role: role.role,
      'budget-hours': formatHours(role.budgetHours),
      'allocated-hours': formatHours(role.allocatedHours || 0),
      'team-allocation': { label: teamAllocationLabel, colors: teamAllocationColors },
      'actual-hours': formatHours(role.actualHours),
      'remaining-hours': formatHours(role.remainingHours),
      'percent-used': formatPercent(role.percentUsed),
      status: { label: statusLabel, colors: statusColors },
      'variance-hours': formatHours(varianceHours),
      'forecast-hours': formatHours(role.budgetHours), // Placeholder - would need actual forecast data
      efficiency: '100%', // Placeholder - would need actual efficiency calculation
      // Raw values for totals calculation
      _raw: {
        budgetHours: role.budgetHours,
        allocatedHours: role.allocatedHours || 0,
        actualHours: role.actualHours,
        remainingHours: role.remainingHours,
        varianceHours,
      },
    };
  });

  // Calculate totals from raw values
  const totals = rows.reduce(
    (acc, row) => ({
      budgetHours: acc.budgetHours + row._raw.budgetHours,
      allocatedHours: acc.allocatedHours + row._raw.allocatedHours,
      actualHours: acc.actualHours + row._raw.actualHours,
      remainingHours: acc.remainingHours + row._raw.remainingHours,
      varianceHours: acc.varianceHours + row._raw.varianceHours,
    }),
    { 
      budgetHours: 0,
      allocatedHours: 0,
      actualHours: 0, 
      remainingHours: 0, 
      varianceHours: 0,
    }
  );

  // Helper function to render cell content based on column ID
  const renderCell = (columnId: string, row: typeof rows[0]) => {
    if (columnId === 'status') {
      return (
        <Badge className={`${row.status.colors.badge} pointer-events-none`}>
          {row.status.label}
        </Badge>
      );
    }
    
    if (columnId === 'team-allocation') {
      return (
        <Badge className={`${row['team-allocation'].colors.badge} pointer-events-none`}>
          {row['team-allocation'].label}
        </Badge>
      );
    }
    
    // Color code negative values for remaining and variance
    if (columnId === 'remaining-hours' && row._raw.remainingHours < 0) {
      return <span className="text-destructive font-semibold">{row[columnId]}</span>;
    }
    
    if (columnId === 'variance-hours' && row._raw.varianceHours !== 0) {
      const isNegative = row._raw.varianceHours < 0;
      return (
        <span className={isNegative ? 'text-destructive font-semibold' : 'text-green-600 font-semibold'}>
          {row[columnId]}
        </span>
      );
    }
    
    return row[columnId as keyof typeof row] as string;
  };

  // Helper function to render total cell content based on column ID
  const renderTotalCell = (columnId: string) => {
    switch (columnId) {
      case 'role':
        return 'TOTAL';
      case 'budget-hours':
        return formatHours(totals.budgetHours);
      case 'allocated-hours':
        return formatHours(totals.allocatedHours);
      case 'team-allocation':
        const totalTeamAllocationStatus = getTeamAllocationStatus(totals.allocatedHours, totals.budgetHours);
        return (
          <Badge className={`${getTeamAllocationStatusColor(totalTeamAllocationStatus).badge} pointer-events-none`}>
            {getTeamAllocationStatusLabel(totalTeamAllocationStatus)}
          </Badge>
        );
      case 'actual-hours':
        return formatHours(totals.actualHours);
      case 'remaining-hours':
        return totals.remainingHours < 0 ? (
          <span className="text-destructive font-semibold">{formatHours(totals.remainingHours)}</span>
        ) : (
          formatHours(totals.remainingHours)
        );
      case 'percent-used':
        return formatPercent((totals.actualHours / totals.budgetHours) * 100);
      case 'variance-hours':
        return totals.varianceHours !== 0 ? (
          <span className={totals.varianceHours < 0 ? 'text-destructive font-semibold' : 'text-green-600 font-semibold'}>
            {formatHours(totals.varianceHours)}
          </span>
        ) : (
          formatHours(totals.varianceHours)
        );
      case 'forecast-hours':
        return formatHours(totals.budgetHours);
      case 'status':
        const totalStatus = getResourceBudgetStatus(totals.actualHours, totals.budgetHours);
        return (
          <Badge className={`${getResourceBudgetStatusColor(totalStatus).badge} pointer-events-none`}>
            {getResourceBudgetStatusLabel(totalStatus)}
          </Badge>
        );
      default:
        return '';
    }
  };

  // Helper function to get alignment class
  const getAlignmentClass = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (columnId === 'status' || columnId === 'team-allocation') return 'text-center';
    return column?.align === 'right' ? 'text-right' : '';
  };

  // Determine unfulfilled hours for assignment (budget - allocated)
  const getUnfulfilledHours = (row: typeof rows[0]) => {
    return row._raw.budgetHours - row._raw.allocatedHours;
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {visibleColumns.map((column) => (
              <TableHead 
                key={column.id} 
                className={`font-bold ${getAlignmentClass(column.id)}`}
              >
                {column.name}
              </TableHead>
            ))}
            {onAssignClick && <TableHead className="text-center font-bold">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const unfulfilledHours = getUnfulfilledHours(row);
            return (
              <TableRow key={row.id}>
                {visibleColumns.map((column) => (
                  <TableCell 
                    key={column.id} 
                    className={`${column.id === 'role' ? 'font-medium' : ''} ${getAlignmentClass(column.id)}`}
                  >
                    {renderCell(column.id, row)}
                  </TableCell>
                ))}
                {onAssignClick && (
                  <TableCell className="text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onAssignClick(row.role, unfulfilledHours)}
                      disabled={unfulfilledHours <= 0}
                      className="gap-1.5"
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Assign
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
          <TableRow className="font-bold bg-muted/50">
            {visibleColumns.map((column) => (
              <TableCell 
                key={column.id} 
                className={getAlignmentClass(column.id)}
              >
                {renderTotalCell(column.id)}
              </TableCell>
            ))}
            {onAssignClick && <TableCell />}
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
